import React, { useCallback, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';

import { useFetch } from 'hooks';
import { compactQuery } from 'utils/queryValues';

// Components
import { Mui } from 'components';
import { ProgressWrapper } from 'components/layouts';

// Local components
import Card from './Card';

const FETCH_OMIT_KEYS = ['page', 'page_size', 'ordering', 'search', 'kpi__in', 'filters'];
const BINARY_PARAM_VALUES = new Set(['true', 'false', '0', '1']);
const EMPTY_DATA = {};
const EMPTY_PARAMS = {};

const useStyles = Mui.makeStyles(theme => ({
  shell: {
    position: 'relative',
    width: '100%',
  },
  track: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
    maxWidth: '100%',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(1),
    WebkitOverflowScrolling: 'touch',
    gap: theme.spacing(1.5),
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  item: {
    flex: '1 1 220px',
    minWidth: 220,
    width: 0,
    display: 'flex',
  },
  arrow: {
    display: 'none',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    width: 32,
    height: 32,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '50%',
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[1],
    [theme.breakpoints.up('sm')]: {
      display: 'inline-flex',
    },
  },
  arrowLeft: {
    left: theme.spacing(0.5),
  },
  arrowRight: {
    right: theme.spacing(0.5),
  },
}));

export const parseKpiIn = value => {
  if (!value) return [];
  return String(value)
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};

const parseMetric = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const quantity = value.quantity ?? value.count;
    const amount = value.amount ?? value.total ?? 0;
    return {
      quantity: quantity == null ? 0 : quantity,
      amount,
    };
  }

  if (value == null || value === '') {
    return { quantity: undefined, amount: undefined };
  }

  return { quantity: undefined, amount: value };
};

const getItemTitle = item => item.title ?? item.label;

const getFilterParamKeys = (items = []) => [
  ...new Set(items.flatMap(item => Object.keys(item.filterParams || {}))),
];

const getVariableFilterKeys = (items = []) =>
  getFilterParamKeys(items).filter(key => {
    const unique = new Set(items.map(item => JSON.stringify(item.filterParams?.[key])));
    return unique.size > 1;
  });

const getConstantFilterParams = (items = []) => {
  const constants = {};

  getFilterParamKeys(items).forEach(key => {
    const values = items.map(item => item.filterParams?.[key]);
    const unique = [...new Set(values.map(value => JSON.stringify(value)))];
    const [constantValue] = values;
    if (unique.length === 1 && constantValue !== undefined) {
      constants[key] = constantValue;
    }
  });

  return constants;
};

const buildFetchParams = (query, items) => {
  const omit = new Set([...FETCH_OMIT_KEYS, ...getVariableFilterKeys(items)]);
  const { filters, ...rest } = query || {};

  return compactQuery(
    Object.fromEntries(
      Object.entries({ ...rest, ...(filters || {}) }).filter(([key]) => !omit.has(key)),
    ),
  );
};

const mergeParamValues = values => {
  const defined = values.filter(value => value !== undefined);
  if (!defined.length) return undefined;

  const nonEmpty = defined.filter(value => value !== '' && value != null);
  if (!nonEmpty.length) return '';

  const unique = [...new Set(nonEmpty.map(String))];
  const looksLikeList = nonEmpty.some(value => String(value).includes(','));
  const allBinary = unique.every(value => BINARY_PARAM_VALUES.has(value));

  if (looksLikeList || !allBinary) {
    const tokens = new Set();
    nonEmpty.forEach(value => parseKpiIn(value).forEach(token => tokens.add(token)));
    return [...tokens].join(',');
  }

  const hasEmpty = defined.some(value => value === '' || value == null);
  if (hasEmpty) return '';
  if (unique.length === 1) return unique[0];
  return '';
};

const mergeSelectedFilterParams = (items, selectedKeys) => {
  const selectedItems = items.filter(item => selectedKeys.includes(item.key));
  if (!selectedItems.length) {
    const cleared = { kpi__in: undefined };
    getVariableFilterKeys(items).forEach(key => {
      cleared[key] = undefined;
    });
    return cleared;
  }

  const merged = {
    ...getConstantFilterParams(items),
    kpi__in: selectedKeys.join(','),
  };

  getVariableFilterKeys(items).forEach(key => {
    merged[key] = mergeParamValues(selectedItems.map(item => item.filterParams?.[key]));
  });

  return merged;
};

const itemsConflict = (current, candidate) => {
  const keys = [...new Set([...(current.conflictKeys || []), ...(candidate.conflictKeys || [])])];
  return keys.some(key => {
    const currentValue = current.filterParams?.[key];
    const nextValue = candidate.filterParams?.[key];
    if (currentValue == null || currentValue === '' || nextValue == null || nextValue === '') {
      return false;
    }
    return String(currentValue) !== String(nextValue);
  });
};

const getCompatibleKeys = (currentKeys, candidate, items) => {
  if (!candidate) return currentKeys;

  return currentKeys.filter(selectedKey => {
    const selectedItem = items.find(item => item.key === selectedKey);
    return !selectedItem || !itemsConflict(selectedItem, candidate);
  });
};

const toggleSelectedKeys = (currentKeys, key, items, selection) => {
  if (selection === 'single') {
    return currentKeys.includes(key) ? [] : [key];
  }

  if (currentKeys.includes(key)) {
    return currentKeys.filter(item => item !== key);
  }

  const candidate = items.find(item => item.key === key);
  return [...getCompatibleKeys(currentKeys, candidate, items), key];
};

const resolveMaybeFn = (value, data, previous, metric) =>
  typeof value === 'function' ? value(data, previous, metric) : value;

const hydrateItem = (item, data = {}, previous) => {
  const usesMetric = item.value === undefined && item.valueKey === undefined;
  const raw = data?.[item.dataKey || item.key];
  const metric = parseMetric(usesMetric ? raw ?? {} : raw);
  const valueFromKey = item.valueKey ? data?.[item.valueKey] : undefined;

  return {
    ...item,
    title: getItemTitle(item),
    value: resolveMaybeFn(item.value, data, previous, metric) ?? valueFromKey,
    amount:
      resolveMaybeFn(item.amount, data, previous, metric) ??
      (usesMetric ? metric.amount : undefined),
    quantity:
      resolveMaybeFn(item.quantity, data, previous, metric) ??
      (usesMetric ? metric.quantity : undefined),
    info: resolveMaybeFn(item.info, data, previous, metric),
    children: resolveMaybeFn(item.children, data, previous, metric),
  };
};

const resolveItemAction = (item, onToggle) => {
  if (typeof item.onClick === 'function' || item.to) {
    return { onClick: item.onClick, to: item.to };
  }
  if (onToggle) {
    return { onClick: () => onToggle(item.key) };
  }
  return {};
};

const getOverflowArrows = node => {
  const { scrollLeft, scrollWidth, clientWidth } = node;
  return {
    left: scrollLeft > 0,
    right: scrollLeft + clientWidth < scrollWidth - 1,
  };
};

const disconnectTrack = (node, onUpdate, observers = {}) => {
  if (node) node.removeEventListener('scroll', onUpdate);
  window.removeEventListener('resize', onUpdate);
  if (observers.resize) observers.resize.disconnect();
  if (observers.mutate) observers.mutate.disconnect();
};

const connectTrack = (node, onUpdate) => {
  node.addEventListener('scroll', onUpdate, { passive: true });
  window.addEventListener('resize', onUpdate);

  const observers = {};

  if (typeof ResizeObserver !== 'undefined') {
    observers.resize = new ResizeObserver(onUpdate);
    observers.resize.observe(node);
  }

  if (typeof MutationObserver !== 'undefined') {
    observers.mutate = new MutationObserver(onUpdate);
    observers.mutate.observe(node, { childList: true });
  }

  onUpdate();
  return observers;
};

function Bar(props) {
  const {
    items = [],
    query,
    onToggle,
    loading = false,
    endpoint,
    params,
    compareEndpoint,
    compareParams,
    setQuery,
    selection = 'multiple',
  } = props;

  // Hooks
  const classes = useStyles();
  const trackRef = useRef(null);
  const trackObserversRef = useRef({});

  // States
  const [showArrows, setShowArrows] = useState({ left: false, right: false });

  // Constants
  const selectedKeys = parseKpiIn(query?.kpi__in);
  const fetchParams = useMemo(
    () => (params !== undefined ? params : buildFetchParams(query, items)),
    [params, query, items],
  );
  const compareUrl = compareEndpoint || (compareParams ? endpoint : null);

  // Fetch
  const { data, isLoading } = useFetch([endpoint || null, fetchParams || EMPTY_PARAMS]);
  const { data: previous, isLoading: isLoadingPrevious } = useFetch([
    compareUrl || null,
    compareParams || EMPTY_PARAMS,
  ]);

  // Constants
  const kpiData = data || EMPTY_DATA;

  const resolvedItems = useMemo(
    () => items.map(item => hydrateItem(item, kpiData, previous)),
    [items, kpiData, previous],
  );

  // Actions
  const handleToggle = useCallback(
    key => {
      if (onToggle) {
        onToggle(key);
        return;
      }

      if (!setQuery) return;

      setQuery(prevQuery => {
        const nextKeys = toggleSelectedKeys(parseKpiIn(prevQuery?.kpi__in), key, items, selection);

        return {
          ...prevQuery,
          ...mergeSelectedFilterParams(items, nextKeys),
          page: 1,
        };
      });
    },
    [items, onToggle, selection, setQuery],
  );

  const toggleHandler = onToggle || setQuery ? handleToggle : undefined;

  const updateArrows = useCallback(() => {
    const node = trackRef.current;
    if (!node) return;

    const next = getOverflowArrows(node);
    setShowArrows(prev => (prev.left === next.left && prev.right === next.right ? prev : next));
  }, []);

  const trackCallbackRef = useCallback(
    node => {
      disconnectTrack(trackRef.current, updateArrows, trackObserversRef.current);
      trackObserversRef.current = {};
      trackRef.current = node;
      if (!node) return;
      trackObserversRef.current = connectTrack(node, updateArrows);
    },
    [updateArrows],
  );

  const handleScroll = direction => {
    const node = trackRef.current;
    if (!node) return;

    const scrollAmount = Math.max(node.clientWidth * 0.7, 180);
    node.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <ProgressWrapper loading={loading || isLoading || isLoadingPrevious}>
      <Mui.Box className={classes.shell}>
        {showArrows.left && (
          <Mui.IconButton
            size="small"
            className={clsx(classes.arrow, classes.arrowLeft)}
            onClick={() => handleScroll('left')}
            aria-label="KPIs anteriores">
            <Mui.Icon>chevron_left</Mui.Icon>
          </Mui.IconButton>
        )}
        <Mui.Box className={classes.track} ref={trackCallbackRef}>
          {resolvedItems.map(item => {
            const { onClick, to } = resolveItemAction(item, toggleHandler);
            const selected =
              item.selected !== undefined ? item.selected : selectedKeys.includes(item.key);

            return (
              <Mui.Box key={item.key} className={classes.item}>
                <Card
                  title={item.title}
                  icon={item.icon}
                  color={item.color}
                  quantity={item.quantity}
                  amount={item.amount}
                  value={item.value}
                  info={item.info}
                  selected={selected}
                  onClick={onClick}
                  to={to}>
                  {item.children}
                </Card>
              </Mui.Box>
            );
          })}
        </Mui.Box>
        {showArrows.right && (
          <Mui.IconButton
            size="small"
            className={clsx(classes.arrow, classes.arrowRight)}
            onClick={() => handleScroll('right')}
            aria-label="Próximos KPIs">
            <Mui.Icon>chevron_right</Mui.Icon>
          </Mui.IconButton>
        )}
      </Mui.Box>
    </ProgressWrapper>
  );
}

Bar.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      color: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.func]),
      amount: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.node,
        PropTypes.func,
      ]),
      value: PropTypes.any,
      valueKey: PropTypes.string,
      dataKey: PropTypes.string,
      info: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.func]),
      children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
      selected: PropTypes.bool,
      onClick: PropTypes.func,
      to: PropTypes.string,
      filterParams: PropTypes.object,
      conflictKeys: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  query: PropTypes.object,
  onToggle: PropTypes.func,
  loading: PropTypes.bool,
  endpoint: PropTypes.string,
  params: PropTypes.object,
  compareEndpoint: PropTypes.string,
  compareParams: PropTypes.object,
  setQuery: PropTypes.func,
  selection: PropTypes.oneOf(['single', 'multiple']),
};

export default Bar;
